import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Volume2, ClipboardCheck, GraduationCap, KeyRound, Users, MapPin, Award, LayoutDashboard, Check, X,
  ChevronDown, Lock, BookOpen, Search, Scale, ArrowRight, PlayCircle,
} from 'lucide-react';
import { useReferral } from '@/hooks/useReferral';
import QualificationFormModal from '@/components/QualificationForm';
import { TEST_CTA, PRICE_ANSWER } from '@/config/membershipCatalog';

/*
  /presentacion — landing de la sesión de presentación (la comparten los brokers con su ?ref=).

  Estructura (Schwartz: mercado en etapa 3 → mecanismo; avatar en conciencia 3-4 → empezar por el deseo/dolor):
    1. Promesa + curiosidad para ver la sesión completa      5. Beneficios del club
    2. Vídeo (Vimeo) + qué vas a descubrir + CTA              6. Encuentros presenciales · 7. Para quién es / no es
    3. Problema del avatar                                    8. Cómo funciona (3 pasos) · 9. Equipo fundador
    4. Solución: el método del club (mecanismo)               10. Preguntas frecuentes · 11. CTA final
  CTA único (Samuel, 10/10/2026): el test de idoneidad (QualificationForm). Al enviarlo se registra el lead
  con el código del broker (?ref, vía useReferral) y aparece el botón para agendar la llamada. Sin precios.
  Landing privada por invitación (noindex): puede concretar cómo es el club por dentro (oportunidades off-market,
  formación, decisión propia), pero sin cifras, rentabilidades, retornos, promesas ni tokens. Amaroo no es «sede».
*/

const VIMEO_ID = '1238277909';  // masterclass del 21/10/2026 (antes 1228421085)

const GOLD = 'text-[#c9a84c]';
const serif = { fontFamily: "'Playfair Display', Georgia, serif" };

const reveal = {
  initial: { opacity: 0, y: 18 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.55, ease: 'easeOut' },
};

const DISCOVER = [
  'Por qué el inmobiliario de calidad ha estado siempre en pocas manos, y qué está cambiando.',
  'Qué es Freedom Homes Club y cómo funciona por dentro.',
  'Cómo se forma un miembro desde cero, antes de tomar cualquier decisión.',
  'Cómo llegan al club las oportunidades off-market y cómo se presentan a los miembros.',
  'Los pasos para formar parte del club.',
];

const PAINS = [
  'Sabes que el mercado inmobiliario de calidad existe, pero siempre lo has visto desde fuera, en círculos a los que no tenías acceso.',
  'Las operaciones serias piden contactos, conocimiento y un tiempo que no tienes para encontrarlas y analizarlas por tu cuenta.',
  'Lo que llega a los portales es lo que el mercado ya ha filtrado. Las buenas operaciones se cierran antes, entre pocas manos.',
  'Y cuando buscas formarte, encuentras teoría que no aplicas o promesas que no te crees.',
];

const METHOD = [
  { icon: BookOpen, step: 'Entiende', text: 'FHC Academy: un recorrido de 7 módulos y 47 clases, desde cero, con un repaso al final de cada módulo y diploma del club.' },
  { icon: Search, step: 'Analiza', text: 'Las oportunidades se presentan dentro del club con su documentación, sus números y su estudio de mercado, con tiempo para revisarlas.' },
  { icon: Scale, step: 'Decide', text: 'Tú eliges en qué participas, y si participas. La decisión es siempre tuya, y la tomas con información.' },
];

const BENEFITS = [
  { icon: GraduationCap, title: 'Formación de principio a fin', text: 'FHC Academy te lleva del primer concepto al análisis de una operación real, a tu ritmo y con acompañamiento.' },
  { icon: KeyRound, title: 'Acceso a oportunidades off-market', text: 'Operaciones inmobiliarias reales que el equipo y sus partners trabajan fuera de los portales, presentadas con su análisis completo en el área privada.' },
  { icon: Users, title: 'Una comunidad con criterio', text: 'Miembros, brokers y el equipo fundador compartiendo análisis, dudas y experiencia en sesiones en directo.' },
  { icon: MapPin, title: 'Eventos y bootcamp presencial', text: 'Encuentros del club y bootcamp presencial en la Costa Blanca, donde la comunidad se conoce cara a cara.' },
  { icon: Award, title: 'Certificación Broker Digital', text: 'Para quien quiere ir más allá: formación práctica y certificación privada del club para presentar el modelo con rigor.' },
  { icon: LayoutDashboard, title: 'Plataforma del miembro', text: 'Tu panel privado con la academia, tus documentos, las oportunidades del club y un asistente de IA disponible 24/7.' },
];

const FOR_YES = [
  'Quieres entender el mercado inmobiliario antes de tomar cualquier decisión.',
  'Buscas acceso a oportunidades que no están en los portales.',
  'Valoras formar parte de una comunidad que comparte criterio.',
  'Te interesa formarte como Broker Digital y presentar el club con rigor.',
  'Prefieres decidir tú, con información, en lugar de delegar a ciegas.',
];
const FOR_NO = [
  'Buscas dinero rápido o resultados garantizados.',
  'Quieres que otra persona decida por ti.',
  'No tienes un rato a la semana para formarte.',
];

const TEAM = [
  { name: 'Antonio Gimeno', role: 'CCO y Director Comercial', img: '/team/antonio-gimeno.jpg' },
  { name: 'Daniel Serrano', role: 'COO', img: '/team/daniel-serrano.jpg' },
  { name: 'Luna Trejos', role: 'Directora de Formación y Comunidad', img: '/team/luna-trejos.jpg' },
];

const FAQ = [
  ['¿Necesito experiencia previa?', 'No. La formación empieza desde cero y avanza a tu ritmo, con acompañamiento del equipo en cada módulo.'],
  ['¿Qué diferencia hay entre los niveles?', 'Todos dan acceso al club, a la formación de FHC Academy, a la comunidad, a las sesiones y a los eventos. Según el nivel, la formación es más completa y, con Premium, se suman el bootcamp presencial y la certificación de Broker Digital.'],
  ['¿Qué incluye exactamente la membresía?', 'La membresía es tu acceso al club: formación, comunidad, eventos y el área privada donde se presentan las oportunidades. Si das el paso en alguna, es una decisión aparte y voluntaria: tú eliges en qué participas, después de formarte y con toda la documentación delante.'],
  ['¿Cuánto cuesta?', `Hay tres niveles de membresía. ${PRICE_ANSWER}`],
  ['¿Puedo formar parte desde fuera de España?', 'Sí. El club está abierto a miembros de España y Latinoamérica. La formación y la comunidad son online, y los eventos presenciales se anuncian con antelación.'],
  ['¿Qué pasa en la llamada?', 'Hablas en privado con un miembro del equipo. Resolvemos tus dudas, te contamos cómo funciona el club por dentro y vemos juntos si encaja contigo. Sin presión.'],
];

function useNoIndex(title) {
  useEffect(() => {
    const prev = document.title;
    document.title = title;
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex';
    document.head.appendChild(meta);
    return () => { document.title = prev; meta.remove(); };
  }, [title]);
}

function CtaButton({ onClick, children = TEST_CTA, size = 'lg', testid = 'cta-test' }) {
  const pad = size === 'lg' ? 'px-7 py-4 text-base' : 'px-5 py-2.5 text-sm';
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`group inline-flex max-w-full items-center justify-center gap-2.5 rounded-full text-center bg-gradient-to-r from-[#c9a84c] to-[#e3c87a] ${pad} font-semibold text-[#0a1225] shadow-[0_10px_40px_-10px_rgba(201,168,76,0.6)] transition-transform hover:-translate-y-0.5`}>
      <ClipboardCheck size={size === 'lg' ? 19 : 16} /> {children}
      <ArrowRight size={size === 'lg' ? 18 : 15} className="transition-transform group-hover:translate-x-0.5" />
    </button>
  );
}

function SectionTitle({ eyebrow, title, sub, center = true }) {
  return (
    <motion.div {...reveal} className={`${center ? 'text-center mx-auto' : ''} max-w-3xl mb-10 md:mb-14`}>
      {eyebrow && <p className={`text-[11px] uppercase tracking-[0.3em] font-semibold mb-3 ${GOLD}`}>{eyebrow}</p>}
      <h2 style={serif} className="text-3xl md:text-[2.6rem] leading-tight text-white">{title}</h2>
      {sub && <p className="mt-4 text-base md:text-lg text-gray-400 leading-relaxed">{sub}</p>}
    </motion.div>
  );
}

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/[0.07]">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
        className="flex min-h-[44px] w-full items-center justify-between gap-4 py-5 text-left text-white hover:text-[#e3c87a]">
        <span className="font-medium">{q}</span>
        <ChevronDown size={18} className={`flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''} ${GOLD}`} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.p initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden pb-5 text-sm leading-relaxed text-gray-400">{a}</motion.p>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Presentacion() {
  const { affiliateName } = useReferral();
  const [muted, setMuted] = useState(true);
  const [testOpen, setTestOpen] = useState(false);
  const [showBar, setShowBar] = useState(false);
  useNoIndex('Sesión privada · Freedom Homes Club');

  useEffect(() => {
    const onScroll = () => setShowBar(window.scrollY > 900);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const open = () => setTestOpen(true);
  const invite = affiliateName ? `${affiliateName} te ha invitado` : 'Has recibido una invitación';

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#060d1b] text-white" data-testid="presentacion">
      {/* Cabecera */}
      <header className="sticky top-0 z-40 border-b border-white/[0.05] bg-[#060d1b]/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3">
          <a href="/" aria-label="Freedom Homes Club"><img src="/logo-fhc-sm.png" alt="Freedom Homes Club" className="h-11 w-auto" /></a>
          <div className="flex items-center gap-4">
            <a href="/dashboard/login" className="hidden text-sm text-gray-400 hover:text-[#c9a84c] sm:inline">Acceder</a>
            <CtaButton onClick={open} size="sm" testid="cta-header">Test de idoneidad</CtaButton>
          </div>
        </div>
      </header>

      {/* 1-2 · Promesa + vídeo */}
      <section className="relative">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-[620px] bg-[radial-gradient(ellipse_at_top,rgba(201,168,76,0.16),transparent_60%)]" />
        <div className="relative mx-auto max-w-5xl px-5 pb-16 pt-10 md:pt-16">
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-center">
            <p className="inline-flex items-center gap-2 rounded-full border border-[#c9a84c]/30 bg-[#c9a84c]/[0.08] px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#e3c87a]">
              <Lock size={12} /> Sesión privada · {invite}
            </p>
            <h1 style={serif} className="mx-auto mt-6 max-w-4xl text-[2.1rem] leading-[1.12] md:text-6xl">
              Las mejores operaciones inmobiliarias nunca llegan a un portal.
              <span className="mt-2 block text-transparent bg-clip-text bg-gradient-to-r from-[#c9a84c] via-[#e3c87a] to-[#c9a84c]">Mira cómo trabaja el club que sí las ve.</span>
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-gray-300 md:text-lg">
              En esta sesión, el equipo fundador de Freedom Homes Club explica el club por dentro: cómo forma a sus miembros,
              cómo funciona su comunidad y cómo se accede a oportunidades inmobiliarias off-market que normalmente quedan
              fuera del alcance de un particular.
            </p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }} className="mt-10">
            <p className="mb-3 flex items-center justify-center gap-2 text-sm text-gray-400">
              <PlayCircle size={16} className={GOLD} /> Busca un rato tranquilo y mírala completa: lo que se explica aquí no cabe en un anuncio.
            </p>
            <div className="relative overflow-hidden rounded-2xl border border-[#c9a84c]/25 bg-black shadow-[0_30px_120px_-30px_rgba(201,168,76,0.45)]">
              <div className="relative" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  src={`https://player.vimeo.com/video/${VIMEO_ID}?autoplay=1&muted=${muted ? 1 : 0}&playsinline=1&title=0&byline=0&portrait=0&pip=0&dnt=1`}
                  className="absolute inset-0 h-full w-full" frameBorder="0" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen
                  title="Sesión de presentación de Freedom Homes Club" data-testid="video" />
              </div>
              {muted && (
                <button type="button" onClick={() => setMuted(false)} data-testid="unmute-video"
                  className="absolute bottom-4 left-4 inline-flex items-center gap-2 rounded-full bg-[#c9a84c] px-4 py-2 text-xs font-semibold text-[#0a1225] shadow-lg hover:bg-[#d4b85c]">
                  <Volume2 size={14} /> Activar sonido
                </button>
              )}
            </div>
          </motion.div>

          <motion.div {...reveal} className="mx-auto mt-10 grid max-w-4xl gap-8 md:grid-cols-[1.3fr_1fr] md:items-center">
            <div>
              <p className={`text-[11px] font-semibold uppercase tracking-[0.25em] ${GOLD}`}>En esta sesión descubrirás</p>
              <ul className="mt-4 space-y-3">
                {DISCOVER.map((d) => (
                  <li key={d} className="flex items-start gap-3 text-gray-300">
                    <span className="mt-1 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-[#c9a84c]/15"><Check size={12} className={GOLD} strokeWidth={3} /></span>
                    <span className="leading-relaxed">{d}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-3xl border border-white/[0.08] bg-white/[0.03] p-6 text-center">
              <p style={serif} className="text-xl text-white">¿Lo has visto?</p>
              <p className="mt-2 text-sm text-gray-400">El siguiente paso es el test de idoneidad: menos de 2 minutos y, al terminar, eliges día y hora para una conversación privada con el equipo.</p>
              <div className="mt-5"><CtaButton onClick={open} size="sm" testid="cta-video">Hacer el test</CtaButton></div>
              <p className="mt-3 text-[11px] text-gray-500">Sin compromiso · Conversación privada</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 3 · Problema */}
      <section className="border-y border-white/[0.05] bg-[#081325] py-20 md:py-28">
        <div className="mx-auto max-w-5xl px-5">
          <SectionTitle eyebrow="Si te suena, sigue leyendo" title={<>Tienes ambición. Tienes criterio. <span className={GOLD}>Lo que te falta es la puerta.</span></>} />
          <div className="grid gap-4 md:grid-cols-2">
            {PAINS.map((p, i) => (
              <motion.div key={p} {...reveal} transition={{ ...reveal.transition, delay: i * 0.06 }}
                className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 text-gray-300 leading-relaxed">
                <span style={serif} className={`mr-2 text-2xl ${GOLD}`}>{String(i + 1).padStart(2, '0')}</span>{p}
              </motion.div>
            ))}
          </div>
          <motion.p {...reveal} style={serif} className="mx-auto mt-12 max-w-2xl text-center text-2xl leading-snug text-white md:text-3xl">
            El problema nunca fuiste tú. Es que el acceso se diseñó para otros.
          </motion.p>
        </div>
      </section>

      {/* 4 · Solución: el método */}
      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-6xl px-5">
          <SectionTitle eyebrow="La solución" title="Un club privado que une lo que el mercado mantiene separado."
            sub="Freedom Homes Club nace en 2026 con una idea sencilla: el mercado inmobiliario que nunca estuvo al alcance de todos, ahora sí. Una persona formada, acompañada y con criterio puede acceder a lo que antes solo veían unos pocos." />
          <div className="relative grid gap-5 md:grid-cols-3">
            <div className="pointer-events-none absolute left-[16%] right-[16%] top-[52px] hidden h-px bg-gradient-to-r from-transparent via-[#c9a84c]/40 to-transparent md:block" />
            {METHOD.map((m, i) => (
              <motion.div key={m.step} {...reveal} transition={{ ...reveal.transition, delay: i * 0.1 }}
                className="relative rounded-3xl border border-white/[0.07] bg-gradient-to-b from-white/[0.04] to-transparent p-7 text-center">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl border border-[#c9a84c]/30 bg-[#0a1628]"><m.icon size={24} className={GOLD} /></div>
                <p className="mt-5 text-[11px] uppercase tracking-[0.3em] text-gray-500">Paso {i + 1}</p>
                <h3 style={serif} className="mt-1 text-3xl text-white">{m.step}</h3>
                <p className="mt-3 text-sm leading-relaxed text-gray-400">{m.text}</p>
              </motion.div>
            ))}
          </div>
          <motion.div {...reveal} className="mt-12 text-center"><CtaButton onClick={open} testid="cta-metodo" /></motion.div>
        </div>
      </section>

      {/* 5 · Beneficios */}
      <section className="border-y border-white/[0.05] bg-[#081325] py-20 md:py-28">
        <div className="mx-auto max-w-6xl px-5">
          <SectionTitle eyebrow="Lo que te da el club" title="Todo lo que necesitas para entrar con criterio." />
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {BENEFITS.map((b, i) => (
              <motion.div key={b.title} {...reveal} transition={{ ...reveal.transition, delay: (i % 3) * 0.08 }}
                className="group rounded-3xl border border-white/[0.06] bg-white/[0.02] p-7 transition-colors hover:border-[#c9a84c]/30">
                <b.icon size={26} className={GOLD} />
                <h3 className="mt-5 text-lg font-semibold text-white">{b.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-gray-400">{b.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 6 · Encuentros presenciales */}
      <section className="py-20 md:py-28">
        <div className="mx-auto grid max-w-6xl items-center gap-10 px-5 md:grid-cols-2">
          <motion.div {...reveal} className="relative">
            <img src="/media/amaroo-sede.jpg" alt="Villa Amaroo, en la Costa Blanca, donde el club ha celebrado encuentros y bootcamps" loading="lazy" className="aspect-[4/3] w-full rounded-3xl border border-white/[0.08] object-cover" />
            <img src="/media/amaroo-aerea.jpg" alt="" loading="lazy" className="absolute -bottom-8 -right-4 hidden aspect-[4/5] w-44 rounded-2xl border-4 border-[#060d1b] object-cover shadow-2xl md:block" />
          </motion.div>
          <motion.div {...reveal}>
            <p className={`text-[11px] font-semibold uppercase tracking-[0.3em] ${GOLD}`}>Encuentros presenciales</p>
            <h2 style={serif} className="mt-3 text-3xl leading-tight md:text-4xl">La comunidad también se encuentra en persona.</h2>
            <p className="mt-4 leading-relaxed text-gray-400">
              El club celebra encuentros y bootcamps presenciales en la Costa Blanca. Villa Amaroo, a pocos minutos de Busot, ha
              acogido encuentros y bootcamps del club: es donde la comunidad se ha conocido, ha trabajado y ha compartido
              criterio cara a cara. El lugar de cada edición se anuncia con antelación.
            </p>
          </motion.div>
        </div>
      </section>

      {/* 7 · Para quién es */}
      <section className="border-y border-white/[0.05] bg-[#081325] py-20 md:py-28">
        <div className="mx-auto max-w-5xl px-5">
          <SectionTitle eyebrow="Para quién es" title="El club no es para todo el mundo. Y está bien que así sea." />
          <div className="grid gap-5 md:grid-cols-[1.4fr_1fr]">
            <motion.div {...reveal} className="rounded-3xl border border-[#c9a84c]/25 bg-[#c9a84c]/[0.05] p-7">
              <p className="text-lg font-semibold text-white">Encaja contigo si…</p>
              <ul className="mt-4 space-y-3">
                {FOR_YES.map((t) => <li key={t} className="flex gap-3 text-gray-300"><Check size={18} className={`mt-0.5 flex-shrink-0 ${GOLD}`} strokeWidth={3} />{t}</li>)}
              </ul>
            </motion.div>
            <motion.div {...reveal} className="rounded-3xl border border-white/[0.06] bg-white/[0.02] p-7">
              <p className="text-lg font-semibold text-white">Probablemente no sea para ti si…</p>
              <ul className="mt-4 space-y-3">
                {FOR_NO.map((t) => <li key={t} className="flex gap-3 text-gray-400"><X size={18} className="mt-0.5 flex-shrink-0 text-gray-500" />{t}</li>)}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 8 · Cómo funciona */}
      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-5xl px-5">
          <SectionTitle eyebrow="Cómo empezar" title="Tres pasos. El primero ya lo has dado." />
          <div className="grid gap-5 md:grid-cols-3">
            {[
              ['Mira la sesión completa', 'Entiende qué es el club y cómo funciona antes de hablar con nadie.'],
              ['Haz el test y agenda tu llamada', 'Menos de 2 minutos de test y, al terminar, eliges día y hora. Un miembro del equipo resuelve tus dudas y vemos juntos si el club encaja contigo.'],
              ['Entra en el club', 'Accedes a tu panel de miembro y empiezas tu formación desde el primer día.'],
            ].map(([t, d], i) => (
              <motion.div key={t} {...reveal} transition={{ ...reveal.transition, delay: i * 0.1 }} className="rounded-3xl border border-white/[0.06] bg-white/[0.02] p-7">
                <span style={serif} className={`text-5xl ${i === 0 ? 'text-[#c9a84c]' : 'text-white/20'}`}>{i + 1}</span>
                <h3 className="mt-3 text-lg font-semibold text-white">{t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-gray-400">{d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 9 · Equipo */}
      <section className="border-y border-white/[0.05] bg-[#081325] py-20 md:py-28">
        <div className="mx-auto max-w-5xl px-5">
          <SectionTitle eyebrow="Quién está detrás" title="Un equipo fundador que da la cara." />
          <div className="mx-auto flex max-w-3xl flex-wrap justify-center gap-5">
            {TEAM.map((p, i) => (
              <motion.div key={p.name} {...reveal} transition={{ ...reveal.transition, delay: i * 0.08 }} className="w-[calc(50%-10px)] text-center md:w-[calc(33.333%-13.34px)]">
                <img src={p.img} alt={p.name} loading="lazy" className="mx-auto aspect-square w-full max-w-[180px] rounded-3xl border border-white/[0.08] object-cover grayscale-[20%]" />
                <p className="mt-4 font-semibold text-white">{p.name}</p>
                <p className="mt-0.5 text-xs text-gray-400">{p.role}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 10 · Preguntas */}
      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-3xl px-5">
          <SectionTitle eyebrow="Preguntas frecuentes" title="Lo que todo el mundo pregunta antes de entrar." />
          <motion.div {...reveal} className="border-t border-white/[0.07]">
            {FAQ.map(([q, a]) => <FaqItem key={q} q={q} a={a} />)}
          </motion.div>
        </div>
      </section>

      {/* 11 · CTA final */}
      <section className="relative overflow-hidden pb-24 pt-6">
        <div className="mx-auto max-w-4xl px-5">
          <motion.div {...reveal} className="relative overflow-hidden rounded-[2rem] border border-[#c9a84c]/30 bg-gradient-to-br from-[#13284a] via-[#0a1628] to-[#060d1b] px-6 py-14 text-center md:px-14">
            <div className="pointer-events-none absolute -top-24 left-1/2 h-64 w-[36rem] -translate-x-1/2 rounded-full bg-[#c9a84c]/20 blur-3xl" />
            <p className={`relative text-[11px] font-semibold uppercase tracking-[0.3em] ${GOLD}`}>Tu invitación está abierta</p>
            <h2 style={serif} className="relative mt-4 text-3xl leading-tight md:text-5xl">El club crece por invitación.<br />La tuya ya está aquí.</h2>
            <p className="relative mx-auto mt-5 max-w-xl text-gray-300">
              {invite} a conocer Freedom Homes Club. El siguiente paso es el test de idoneidad y, después, una conversación privada y sin compromiso con el equipo.
            </p>
            <div className="relative mt-8"><CtaButton onClick={open} testid="cta-final" /></div>
          </motion.div>
        </div>
      </section>

      <footer className="border-t border-white/[0.05] py-10">
        <div className="mx-auto max-w-4xl space-y-3 px-5 text-center text-[11px] leading-relaxed text-gray-500">
          <p>
            Freedom Homes Club es un club privado de formación y comunidad. La información de esta página es divulgativa:
            no constituye una oferta pública ni garantiza resultados. Participar en cualquier oportunidad del club es una
            decisión independiente de la membresía, voluntaria y sujeta a su documentación.
          </p>
          <p>© {new Date().getFullYear()} Freedom Life Club LLC · info@freedomhomes.club · <a href="/terms-conditions" className="hover:text-[#c9a84c]">Términos y condiciones</a> · <a href="/privacy-policy-fhc" className="hover:text-[#c9a84c]">Privacidad</a></p>
        </div>
      </footer>

      {/* Barra fija en móvil */}
      <AnimatePresence>
        {showBar && !testOpen && (
          <motion.div initial={{ y: 100 }} animate={{ y: 0 }} exit={{ y: 100 }} transition={{ duration: 0.25 }}
            className="fixed inset-x-0 bottom-0 z-50 border-t border-[#c9a84c]/20 bg-[#060d1b]/95 px-4 py-3 backdrop-blur md:hidden">
            <button type="button" onClick={open} data-testid="cta-sticky"
              className="flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#c9a84c] to-[#e3c87a] py-3.5 font-semibold text-[#0a1225]">
              <ClipboardCheck size={17} /> {TEST_CTA}
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <QualificationFormModal isOpen={testOpen} onClose={() => setTestOpen(false)} source="presentacion" lang="es" />
    </div>
  );
}
